import type {
  BulkAccessCopiesChoice,
  BulkAccessItemResult,
  BulkAccessMode,
  BulkAccessPreview,
  BulkAccessRequest,
  BulkAccessResult,
  BulkAccessScope,
  BulkAccessTarget,
} from '~/features/access-control/types/AccessControl';

import type { ServerAccessGrant } from './accessGrantMapper';

import { mapGrantToServer, mapServerGrant } from './accessGrantMapper';

type ServerBulkAccessTarget = {
  id: string;
  type: 'folder' | 'document';
  name: string;
};

export type ServerBulkAccessPreview = {
  folder_count: number;
  document_count: number;
  mixed_state_count: number;
  copies_count: number;
};

type ServerBulkAccessItemResult = {
  id: string;
  type: 'folder' | 'document';
  name: string;
  prior_grants_removed?: number;
  access_summary?: string;
};

export type ServerBulkAccessResult = {
  mode: BulkAccessMode;
  scope: BulkAccessScope;
  updated_count: number;
  items: ServerBulkAccessItemResult[];
  grants_applied: ServerAccessGrant[];
  location_label?: string;
};

export type ServerBulkAccessRequest = {
  targets: ServerBulkAccessTarget[];
  scope: BulkAccessScope;
  mode: BulkAccessMode;
  copies_choice: BulkAccessCopiesChoice;
  grants: ServerAccessGrant[];
};

function mapTargetToServer(target: BulkAccessTarget): ServerBulkAccessTarget {
  return {
    id: target.id,
    type: target.type,
    name: target.name,
  };
}

function mapItemResultFromServer(
  item: ServerBulkAccessItemResult,
): BulkAccessItemResult {
  return {
    id: item.id,
    type: item.type,
    name: item.name,
    priorGrantsRemoved: item.prior_grants_removed,
    accessSummary: item.access_summary,
  };
}

export function mapBulkAccessPreviewRequest(targets: BulkAccessTarget[]) {
  return {
    targets: targets.map(mapTargetToServer),
  };
}

export function mapBulkAccessPreviewResponse(
  serverResponse: ServerBulkAccessPreview,
): BulkAccessPreview {
  return {
    folderCount: serverResponse.folder_count,
    documentCount: serverResponse.document_count,
    mixedStateCount: serverResponse.mixed_state_count,
    copiesCount: serverResponse.copies_count,
  };
}

export function mapBulkAccessRequest(
  request: BulkAccessRequest,
): ServerBulkAccessRequest {
  return {
    targets: request.targets.map(mapTargetToServer),
    scope: request.scope,
    mode: request.mode,
    copies_choice: request.copiesChoice,
    grants: request.grants.map(mapGrantToServer),
  };
}

export function mapBulkAccessResultResponse(
  serverResponse: ServerBulkAccessResult,
): BulkAccessResult {
  return {
    mode: serverResponse.mode,
    scope: serverResponse.scope,
    updatedCount: serverResponse.updated_count,
    items: serverResponse.items.map(mapItemResultFromServer),
    grantsApplied: serverResponse.grants_applied.map(mapServerGrant),
    locationLabel: serverResponse.location_label,
  };
}
